import { Cross2Icon } from "@radix-ui/react-icons";
import { Box, Flex, IconButton, RadioGroup, Text, Tooltip } from "@radix-ui/themes";
import { Control, Controller, FieldErrors, FieldValues, Path } from "react-hook-form";
import {
  ComposedName,
  FieldShell,
  fieldAria,
  fieldIds,
  resolveFieldError,
} from "../FieldShell";

interface RadioGroupProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  control: Control<any>;
  errors: FieldErrors<any>;
  style?: any;
  fieldProps?: any;
  options: { value: string; label: string }[];
  itemsLoading?: boolean;
  itemsError?: boolean;
  composedName?: ComposedName;
  required?: boolean;
  hint?: string;
}

const RadioGroupComponent = <T extends FieldValues>({
  name,
  label,
  control,
  errors,
  style,
  fieldProps,
  options,
  composedName,
  itemsError,
  itemsLoading,
  required,
  hint,
}: RadioGroupProps<T>) => {
  const error = resolveFieldError(errors, name, composedName);
  const { fieldId } = fieldIds(name);

  return (
    <Box {...style}>
      <FieldShell
        name={name}
        label={label}
        required={required}
        error={error}
        hint={hint}
        labelAs="div"
      >
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            if (itemsError) {
              return (
                <Text as="p" size="1" color="red">
                  Erro ao carregar as opções.
                </Text>
              );
            }

            if (itemsLoading) {
              return (
                <Text as="p" size="1" color="gray">
                  A carregar…
                </Text>
              );
            }

            if (!options?.length) {
              return (
                <Text as="p" size="1" color="gray">
                  Sem opções disponíveis
                </Text>
              );
            }

            return (
              <Flex align="center" gap="3" wrap="wrap">
                <RadioGroup.Root
                  variant="surface"
                  name={field.name}
                  onValueChange={field.onChange}
                  value={`${field.value ?? ""}`}
                  aria-labelledby={fieldId}
                  {...fieldAria(name, error, hint)}
                  {...fieldProps}
                >
                  {options.map((option) => (
                    <RadioGroup.Item key={option.value} value={`${option.value}`}>
                      {option.label}
                    </RadioGroup.Item>
                  ))}
                </RadioGroup.Root>

                {field.value && !fieldProps?.disabled && (
                  <Tooltip content="Limpar selecção">
                    <IconButton
                      type="button"
                      variant="ghost"
                      color="gray"
                      size="1"
                      aria-label={`Limpar ${label}`}
                      onClick={() => field.onChange("")}
                    >
                      <Cross2Icon />
                    </IconButton>
                  </Tooltip>
                )}
              </Flex>
            );
          }}
        />
      </FieldShell>
    </Box>
  );
};

export default RadioGroupComponent;
